import {
  combineReducers,
  configureStore,
  type Reducer,
  type UnknownAction,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

// RTK Query APIs
import { companyAnnouncementApi } from "@/services/(company)/announcementApi";
import { appGuidesApi } from "@/services/api.app-guides";
import { companyAssetsApi } from "@/services/(company)/api.assets";
import { companyPoliciesApi } from "@/services/(company)/api.company-policies";
import { companyDocumentTypesApi } from "@/services/(company)/api.document-types";
import { companyDocumentsApi } from "@/services/(company)/api.documents";
import { companyEventsApi } from "@/services/(company)/api.events";
import { helpAndSupportApi } from "@/services/(company)/api.help-and-support";
import { companyNfcFormsApi } from "@/services/(company)/api.nfc-forms";
import { companyOffboardingApi } from "@/services/(company)/api.offboarding";
import { companyOnboardingSettingsApi } from "@/services/(company)/api.onboarding-settings";
import { companySetupApi } from "@/services/(company)/api.company-setup";
import { companyOvertimeApi } from "@/services/(company)/api.overtime";
import { companyOvertimePoliciesApi } from "@/services/(company)/api.overtime-policies";
import { performanceApi } from "@/services/(company)/api.performance";
import { companyProjectsApi } from "@/services/(company)/api.projects";
import { companyTeamsApi } from "@/services/(company)/api.teams";
import { companyTimeAttendanceApi } from "@/services/(company)/api.time-attendance";
import { companyAssetBrandsApi } from "@/services/(company)/assetBrandsApi";
import { companyAssetTypesApi } from "@/services/(company)/assetTypesApi";
import { companyAttendanceRegularizationApi } from "@/services/(company)/attendanceRegularizationApi";
import { attendanceRulesV2Api } from "@/services/(company)/attendanceRulesV2Api";
import { companyBenefitsApi } from "@/services/(company)/benefitsApi";
import { companyBlackoutPeriodApi } from "@/services/(company)/blackoutPeriodApi";
import { companyBranchConfigsApi } from "@/services/(company)/branchConfigsApi";
import { companyBranchesApi } from "@/services/(company)/branchesApi";
import { companyCarryForwardPoliciesApi } from "@/services/(company)/carryForwardPoliciesApi";
import { companyCheckinCheckoutConfigApi } from "@/services/(company)/checkinCheckoutConfigApi";
import { companyRegularizationSettingsApi } from "@/services/(company)/regularizationSettingsApi";
import { companyCountryRulesApi } from "@/services/(company)/countryRulesApi";
import { companyDeductionsApi } from "@/services/(company)/deductionsApi";
import { companyDepartmentsApi } from "@/services/(company)/departmentsApi";
import { documentSignaturesApi } from "@/services/(company)/documentSignaturesApi";
import { documentTemplatesApi } from "@/services/(company)/documentTemplatesApi";
import { emailTemplatesApi } from "@/services/(company)/emailTemplatesApi";
import { myAppApi } from "@/services/(company)/api.my-app";
import { myAppMySpaceApi } from "@/services/(my-space)/api.my-app";
import { companyFormsApi } from "@/services/(company)/formsApi";
import { companyEarningsApi } from "@/services/(company)/earningsApi";
import { companyEarningTypesApi } from "@/services/(company)/earningTypesApi";
import { companyEnchashmentPolicyApi } from "@/services/(company)/enchashmentPolicyApi";
import { companyDataTransferApi } from "@/services/(company)/dataTransferApi";
import { dashboardLayoutApi } from "@/services/(company)/api.dashboard-layout";
import { companyFilesApi } from "@/services/(company)/filesApi";
import { companyHolidaysApi } from "@/services/(company)/holidaysApi";
import { companyLeaveRequestApi } from "@/services/(company)/leaveRequestApi";
import { companyLeaveEntitlementsApi } from "@/services/(company)/leavesEntitlementsApi";
import { companyLeaveTypesApi } from "@/services/(company)/leaveTypesApi";
import { companyLeaveGroupsApi } from "@/services/(company)/leaveGroupsApi";
import { payrollDisputesApi } from "@/services/(company)/payrollDisputesApi";
import { companyPayrollPoliciesApi } from "@/services/(company)/payrollPoliciesApi";
import { payrollReportsApi } from "@/services/(company)/payrollReportsApi";
import { companyPayrollRunsApi } from "@/services/(company)/payrollRunsApi";
import { companyPositionsApi } from "@/services/(company)/positionsApi";
import { companyQuickActivityApi } from "@/services/(company)/quickActivityApi";
import { companyRolesApi } from "@/services/(company)/rolesApi";
import { companyShiftApi } from "@/services/(company)/shiftApi";
import { subscriptionApi } from "@/services/(company)/subscriptionApi";
import { companyUserApi } from "@/services/(company)/userApi";
import { companyWorkflowApi } from "@/services/(company)/workflowApi";
import { assetsApi } from "@/services/(my-space)/api.assets";
import { MSCompanyPoliciesApi } from "@/services/(my-space)/api.company-policies";
import { myspaceDocumentTypesApi } from "@/services/(my-space)/api.document-types";
import { documentsApi } from "@/services/(my-space)/api.documents";
import { leaveRequestApi } from "@/services/(my-space)/api.leaves";
import { msOffboardingApi } from "@/services/(my-space)/api.offboarding";
import { msOnboardingSettingsApi } from "@/services/(my-space)/api.onboarding-settings";
import { msOvertimeApi } from "@/services/(my-space)/api.overtime";
import { msTeamsApi } from "@/services/(my-space)/api.teams";
import { timeAttendanceApi } from "@/services/(my-space)/api.time-attendance";
import { attendanceRegularizationApi } from "@/services/(my-space)/attendanceRegularizationApi";
import { companiesApi } from "@/services/(my-space)/companiesApi";
import { countriesApi } from "@/services/(my-space)/countriesApi";
import { employeePayrollApi } from "@/services/(my-space)/employeePayrollApi";
import { notificationApi } from "@/services/(my-space)/notificationApi";
import { mySpaceQuickActivityApi } from "@/services/(my-space)/quickActivityApi";
import { myPermissionsApi } from "@/services/(my-space)/permissionsApi";
import { userApi } from "@/services/(my-space)/userApi";
import { signLinkApi } from "@/services/(public)/signLinkApi";
import { eventRsvpApi } from "@/services/(public)/eventRsvpApi";
import { announcementApi } from "@/services/api.announcements";
import { fileUploadApi } from "@/services/api.file-upload";
import { publicNfcFormsApi } from "@/services/api.public-nfc-forms";
import { msDigitalIdApi } from "@/services/(my-space)/api.digital-id";
import { companyDigitalIdSettingsApi } from "@/services/(company)/api.digital-id-settings";
import { companyDigitalIdApi } from "@/services/(company)/api.digital-id";
import { publicDigitalIdApi } from "@/services/api.public-digital-id";
import { companyRecruitmentApi } from "@/services/(company)/api.recruitment";
import { recruitmentInterviewsApi } from "@/services/(company)/api.recruitment-interviews";
import { recruitmentAssessmentsApi } from "@/services/(company)/api.recruitment-assessments";
import { recruitmentOffersApi } from "@/services/(company)/api.recruitment-offers";
import { employeeReferralsApi } from "@/services/(my-space)/api.referrals";
import { authApi } from "@/services/authApi";
import { notificationsApi } from "@/services/notificationsApi";
import { searchLocationApi } from "@/services/searchLocationApi";
import { weatherApi } from "@/services/weatherApi";
// Slices
import { companyAiAssistantApi } from "@/services/(company)/api.ai-assistant";
import { companyExpensesApi } from "@/services/(company)/api.expenses";
import { companySalesApi } from "@/services/(company)/api.sales";
import { companyTasksApi } from "@/services/(company)/api.tasks";
import { companyIntegrationsApi } from "@/services/(company)/api.integrations";
import { companyAuditLogsApi } from "@/services/(company)/api.audit-logs";
import { companyBiometricDevicesApi } from "@/services/(company)/biometricDevicesApi";
import { companyAddOnsApi } from "@/services/(company)/addOnsApi";
import { aiAssistantApi } from "@/services/(my-space)/api.ai-assistant";
import { mySpaceEventsApi } from "@/services/(my-space)/api.events";
import { msExpensesApi } from "@/services/(my-space)/api.expenses";
import { msSalesApi } from "@/services/(my-space)/api.sales";
import { msTasksApi } from "@/services/(my-space)/api.tasks";
import { chatApi } from "@/services/(my-space)/api.chat";
import { aiAPIService } from "@/services/ai.api";
import authSlice, { AUTH_SLICE_NAME } from "@/store/slices/auth.slice";
import connectionStatusSlice, {
  CONNECTION_STATUS_SLICE_NAME,
} from "@/store/slices/connection-status.slice";
import splitScreenSlice, {
  SPLIT_SCREEN_SLIC_NAME,
} from "@/store/slices/split-screen.slice";
import timeAttendanceSlice from "@/store/slices/time-attendance.slice";
import userSlice, { USER_SLICE_NAME } from "@/store/slices/user.slice";
import { LOGOUT_USER_ACTION } from "@/utils/enums/auth.enums";

const persistConfig = {
  key: "root",
  storage: storage,
  whitelist: ["user"],
};

const appReducer = combineReducers({
  // RTK Query APIs
  [authApi.reducerPath]: authApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [attendanceRegularizationApi.reducerPath]:
    attendanceRegularizationApi.reducer,
  [assetsApi.reducerPath]: assetsApi.reducer,
  [weatherApi.reducerPath]: weatherApi.reducer,
  [searchLocationApi.reducerPath]: searchLocationApi.reducer,
  [documentsApi.reducerPath]: documentsApi.reducer,
  [mySpaceEventsApi.reducerPath]: mySpaceEventsApi.reducer,
  [aiAssistantApi.reducerPath]: aiAssistantApi.reducer,
  [companyAiAssistantApi.reducerPath]: companyAiAssistantApi.reducer,
  [companyExpensesApi.reducerPath]: companyExpensesApi.reducer,
  [msExpensesApi.reducerPath]: msExpensesApi.reducer,
  [companySalesApi.reducerPath]: companySalesApi.reducer,
  [msSalesApi.reducerPath]: msSalesApi.reducer,
  [companyTasksApi.reducerPath]: companyTasksApi.reducer,
  [companyIntegrationsApi.reducerPath]: companyIntegrationsApi.reducer,
  [companyAuditLogsApi.reducerPath]: companyAuditLogsApi.reducer,
  [msTasksApi.reducerPath]: msTasksApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [companyBiometricDevicesApi.reducerPath]: companyBiometricDevicesApi.reducer,
  [companyAddOnsApi.reducerPath]: companyAddOnsApi.reducer,
  [companyLeaveEntitlementsApi.reducerPath]:
    companyLeaveEntitlementsApi.reducer,
  [companyAttendanceRegularizationApi.reducerPath]:
    companyAttendanceRegularizationApi.reducer,
  [companyCarryForwardPoliciesApi.reducerPath]:
    companyCarryForwardPoliciesApi.reducer,
  [companyEnchashmentPolicyApi.reducerPath]:
    companyEnchashmentPolicyApi.reducer,
  [countriesApi.reducerPath]: countriesApi.reducer,
  [companiesApi.reducerPath]: companiesApi.reducer,
  [companyCheckinCheckoutConfigApi.reducerPath]:
    companyCheckinCheckoutConfigApi.reducer,
  [companyRegularizationSettingsApi.reducerPath]:
    companyRegularizationSettingsApi.reducer,
  [attendanceRulesV2Api.reducerPath]: attendanceRulesV2Api.reducer,
  [notificationApi.reducerPath]: notificationApi.reducer,
  [companyUserApi.reducerPath]: companyUserApi.reducer,
  [leaveRequestApi.reducerPath]: leaveRequestApi.reducer,
  [companyShiftApi.reducerPath]: companyShiftApi.reducer,
  [companyRolesApi.reducerPath]: companyRolesApi.reducer,
  [myPermissionsApi.reducerPath]: myPermissionsApi.reducer,
  [companyAssetsApi.reducerPath]: companyAssetsApi.reducer,
  [companyEventsApi.reducerPath]: companyEventsApi.reducer,
  [msTeamsApi.reducerPath]: msTeamsApi.reducer,
  [companyTeamsApi.reducerPath]: companyTeamsApi.reducer,
  [companyProjectsApi.reducerPath]: companyProjectsApi.reducer,
  [companyBenefitsApi.reducerPath]: companyBenefitsApi.reducer,
  [timeAttendanceApi.reducerPath]: timeAttendanceApi.reducer,
  [companyBranchesApi.reducerPath]: companyBranchesApi.reducer,
  [companyEarningsApi.reducerPath]: companyEarningsApi.reducer,
  [companyPositionsApi.reducerPath]: companyPositionsApi.reducer,
  [companyLeaveTypesApi.reducerPath]: companyLeaveTypesApi.reducer,
  [companyLeaveGroupsApi.reducerPath]: companyLeaveGroupsApi.reducer,
  [companyDeductionsApi.reducerPath]: companyDeductionsApi.reducer,
  [companyAssetTypesApi.reducerPath]: companyAssetTypesApi.reducer,
  [companyDocumentsApi.reducerPath]: companyDocumentsApi.reducer,
  [companyDocumentTypesApi.reducerPath]: companyDocumentTypesApi.reducer,
  [myspaceDocumentTypesApi.reducerPath]: myspaceDocumentTypesApi.reducer,
  [documentTemplatesApi.reducerPath]: documentTemplatesApi.reducer,
  [emailTemplatesApi.reducerPath]: emailTemplatesApi.reducer,
  [myAppApi.reducerPath]: myAppApi.reducer,
  [myAppMySpaceApi.reducerPath]: myAppMySpaceApi.reducer,
  [signLinkApi.reducerPath]: signLinkApi.reducer,
  [eventRsvpApi.reducerPath]: eventRsvpApi.reducer,
  [documentSignaturesApi.reducerPath]: documentSignaturesApi.reducer,
  [companyAssetBrandsApi.reducerPath]: companyAssetBrandsApi.reducer,
  [companyDepartmentsApi.reducerPath]: companyDepartmentsApi.reducer,
  [companyEarningTypesApi.reducerPath]: companyEarningTypesApi.reducer,
  [companyLeaveRequestApi.reducerPath]: companyLeaveRequestApi.reducer,
  [companyBlackoutPeriodApi.reducerPath]: companyBlackoutPeriodApi.reducer,
  [companyHolidaysApi.reducerPath]: companyHolidaysApi.reducer,
  [companyTimeAttendanceApi.reducerPath]: companyTimeAttendanceApi.reducer,
  [dashboardLayoutApi.reducerPath]: dashboardLayoutApi.reducer,
  [fileUploadApi.reducerPath]: fileUploadApi.reducer,
  [companyDataTransferApi.reducerPath]: companyDataTransferApi.reducer,
  [companyFilesApi.reducerPath]: companyFilesApi.reducer,
  [aiAPIService.reducerPath]: aiAPIService.reducer,
  [companyWorkflowApi.reducerPath]: companyWorkflowApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [announcementApi.reducerPath]: announcementApi.reducer,
  [companyAnnouncementApi.reducerPath]: companyAnnouncementApi.reducer,
  [MSCompanyPoliciesApi.reducerPath]: MSCompanyPoliciesApi.reducer,
  [companyPoliciesApi.reducerPath]: companyPoliciesApi.reducer,
  [helpAndSupportApi.reducerPath]: helpAndSupportApi.reducer,
  [appGuidesApi.reducerPath]: appGuidesApi.reducer,
  [subscriptionApi.reducerPath]: subscriptionApi.reducer,
  [performanceApi.reducerPath]: performanceApi.reducer,
  [companyQuickActivityApi.reducerPath]: companyQuickActivityApi.reducer,
  [mySpaceQuickActivityApi.reducerPath]: mySpaceQuickActivityApi.reducer,
  [companyPayrollPoliciesApi.reducerPath]: companyPayrollPoliciesApi.reducer,
  [companyCountryRulesApi.reducerPath]: companyCountryRulesApi.reducer,
  [companyBranchConfigsApi.reducerPath]: companyBranchConfigsApi.reducer,
  [companyPayrollRunsApi.reducerPath]: companyPayrollRunsApi.reducer,
  [payrollReportsApi.reducerPath]: payrollReportsApi.reducer,
  [payrollDisputesApi.reducerPath]: payrollDisputesApi.reducer,
  [employeePayrollApi.reducerPath]: employeePayrollApi.reducer,
  [companyOffboardingApi.reducerPath]: companyOffboardingApi.reducer,
  [msOffboardingApi.reducerPath]: msOffboardingApi.reducer,
  [msOnboardingSettingsApi.reducerPath]: msOnboardingSettingsApi.reducer,
  [companyOnboardingSettingsApi.reducerPath]:
    companyOnboardingSettingsApi.reducer,
  [companySetupApi.reducerPath]: companySetupApi.reducer,
  [msOvertimeApi.reducerPath]: msOvertimeApi.reducer,
  [companyOvertimeApi.reducerPath]: companyOvertimeApi.reducer,
  [companyOvertimePoliciesApi.reducerPath]: companyOvertimePoliciesApi.reducer,
  [companyNfcFormsApi.reducerPath]: companyNfcFormsApi.reducer,
  [companyFormsApi.reducerPath]: companyFormsApi.reducer,
  [publicNfcFormsApi.reducerPath]: publicNfcFormsApi.reducer,
  [msDigitalIdApi.reducerPath]: msDigitalIdApi.reducer,
  [companyDigitalIdSettingsApi.reducerPath]:
    companyDigitalIdSettingsApi.reducer,
  [companyDigitalIdApi.reducerPath]: companyDigitalIdApi.reducer,
  [publicDigitalIdApi.reducerPath]: publicDigitalIdApi.reducer,
  [companyRecruitmentApi.reducerPath]: companyRecruitmentApi.reducer,
  [recruitmentInterviewsApi.reducerPath]: recruitmentInterviewsApi.reducer,
  [recruitmentAssessmentsApi.reducerPath]: recruitmentAssessmentsApi.reducer,
  [recruitmentOffersApi.reducerPath]: recruitmentOffersApi.reducer,
  [employeeReferralsApi.reducerPath]: employeeReferralsApi.reducer,
  //  Slices
  [AUTH_SLICE_NAME]: authSlice,
  [USER_SLICE_NAME]: userSlice,
  [SPLIT_SCREEN_SLIC_NAME]: splitScreenSlice,
  [CONNECTION_STATUS_SLICE_NAME]: connectionStatusSlice,
  timeAttendance: timeAttendanceSlice,
});

const rootReducer: Reducer = (state: RootState, action: UnknownAction) => {
  if (action.type === LOGOUT_USER_ACTION.NAME) {
    // Resetting the entire store upon user logout
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(
      authApi.middleware,
      userApi.middleware,
      assetsApi.middleware,
      weatherApi.middleware,
      searchLocationApi.middleware,
      documentsApi.middleware,
      mySpaceEventsApi.middleware,
      aiAssistantApi.middleware,
      companyAiAssistantApi.middleware,
      countriesApi.middleware,
      companiesApi.middleware,
      notificationApi.middleware,
      companyUserApi.middleware,
      leaveRequestApi.middleware,
      companyRolesApi.middleware,
      myPermissionsApi.middleware,
      companyShiftApi.middleware,
      companyEventsApi.middleware,
      msTeamsApi.middleware,
      companyTeamsApi.middleware,
      companyProjectsApi.middleware,
      companyAssetsApi.middleware,
      timeAttendanceApi.middleware,
      companyBenefitsApi.middleware,
      companyBranchesApi.middleware,
      companyEarningsApi.middleware,
      companyPositionsApi.middleware,
      companyAssetTypesApi.middleware,
      companyDocumentsApi.middleware,
      companyDocumentTypesApi.middleware,
      myspaceDocumentTypesApi.middleware,
      documentTemplatesApi.middleware,
      emailTemplatesApi.middleware,
      myAppApi.middleware,
      myAppMySpaceApi.middleware,
      signLinkApi.middleware,
      eventRsvpApi.middleware,
      documentSignaturesApi.middleware,
      companyLeaveTypesApi.middleware,
      companyLeaveGroupsApi.middleware,
      companyDeductionsApi.middleware,
      companyDepartmentsApi.middleware,
      companyAssetBrandsApi.middleware,
      companyEarningTypesApi.middleware,
      companyLeaveRequestApi.middleware,
      companyTimeAttendanceApi.middleware,
      dashboardLayoutApi.middleware,
      companyBlackoutPeriodApi.middleware,
      companyHolidaysApi.middleware,
      attendanceRegularizationApi.middleware,
      companyLeaveEntitlementsApi.middleware,
      companyEnchashmentPolicyApi.middleware,
      attendanceRulesV2Api.middleware,
      companyCarryForwardPoliciesApi.middleware,
      companyCheckinCheckoutConfigApi.middleware,
      companyRegularizationSettingsApi.middleware,
      companyAttendanceRegularizationApi.middleware,
      fileUploadApi.middleware,
      companyDataTransferApi.middleware,
      companyFilesApi.middleware,
      aiAPIService.middleware,
      companyWorkflowApi.middleware,
      notificationsApi.middleware,
      announcementApi.middleware,
      companyAnnouncementApi.middleware,
      MSCompanyPoliciesApi.middleware,
      companyPoliciesApi.middleware,
      helpAndSupportApi.middleware,
      appGuidesApi.middleware,
      subscriptionApi.middleware,
      performanceApi.middleware,
      companyQuickActivityApi.middleware,
      mySpaceQuickActivityApi.middleware,
      companyPayrollPoliciesApi.middleware,
      companyCountryRulesApi.middleware,
      companyBranchConfigsApi.middleware,
      companyPayrollRunsApi.middleware,
      payrollReportsApi.middleware,
      payrollDisputesApi.middleware,
      employeePayrollApi.middleware,
      companyOffboardingApi.middleware,
      msOffboardingApi.middleware,
      msOnboardingSettingsApi.middleware,
      companyOnboardingSettingsApi.middleware,
      companySetupApi.middleware,
      msOvertimeApi.middleware,
      companyOvertimeApi.middleware,
      companyOvertimePoliciesApi.middleware,
      companyNfcFormsApi.middleware,
      companyFormsApi.middleware,
      publicNfcFormsApi.middleware,
      msDigitalIdApi.middleware,
      companyDigitalIdSettingsApi.middleware,
      companyDigitalIdApi.middleware,
      publicDigitalIdApi.middleware,
      companyRecruitmentApi.middleware,
      recruitmentInterviewsApi.middleware,
      recruitmentAssessmentsApi.middleware,
      recruitmentOffersApi.middleware,
      employeeReferralsApi.middleware,
      companyExpensesApi.middleware,
      msExpensesApi.middleware,
      companySalesApi.middleware,
      msSalesApi.middleware,
      companyTasksApi.middleware,
      companyIntegrationsApi.middleware,
      companyAuditLogsApi.middleware,
      msTasksApi.middleware,
      chatApi.middleware,
      companyBiometricDevicesApi.middleware,
      companyAddOnsApi.middleware,
    ),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
// const persistor = persistStore(store);

/*
 * Warms up every RTK Query middleware while the call stack is still empty.
 *
 * Each RTKQ middleware self-registers on the first action it ever sees by
 * dispatching `middlewareRegistered`, and that nested dispatch re-enters the chain
 * from the top — so initialising N slices costs O(N²) stack frames in one
 * synchronous burst. With the ~90 slices registered above that burst is >4,000
 * frames, and it lands on whichever component issues the first query of the page
 * load. Fired from inside a deep render tree it overflows the stack outright
 * (`RangeError: Maximum call stack size exceeded`, recovered by React only via a
 * full synchronous re-render).
 *
 * Throwaway dispatches drain that cascade here instead, so no component pays it
 * mid-render. The action type matches no reducer and no middleware handler, so
 * nothing else observes it, and an aborted pass costs nothing — which is what makes
 * this safe to retry.
 *
 * Retried because one pass does not fit: the burst is deep enough to exhaust the
 * stack on its own, and each pass registers as many middlewares as it has room for
 * before unwinding. Every subsequent pass therefore starts with fewer left to
 * register and goes deeper, converging in a handful of rounds. Each runs in its own
 * macrotask, both for a full stack budget and to keep boot non-blocking.
 */
const warmUpQueryMiddleware = (remainingAttempts: number): void => {
  try {
    store.dispatch({ type: "@@rtkq/warmupMiddleware" });
  } catch {
    // Stack exhausted mid-cascade. Whatever registered stays registered, so the
    // next pass is strictly shorter.
    if (remainingAttempts > 0) {
      setTimeout(() => warmUpQueryMiddleware(remainingAttempts - 1), 0);
    }
  }
};

setTimeout(() => warmUpQueryMiddleware(20), 0);

setupListeners(store.dispatch);
